/**
 * How the live view reaches the purchase's browser, whichever it is: the
 * view draws what it is sent and passes the buyer's finger and keyboard
 * back through one of these. Browser Use's Chrome is driven over CDP from
 * the buyer's phone (cdp.ts); a Boat browser, Camoufox, has no CDP, and is
 * shown by the live view on its own machine (boat.ts). The credential's
 * shape says which (`isLiveViewHost`).
 */
import { isBoatLiveView, openCdpTransport } from "./cdp";
import { openBoatTransport } from "./boat";

/** A point in the shown part of the remote page, in its CSS pixels. */
export type Point = { x: number; y: number };

/** One image of the remote page: base64 `data`, and the size of the part of
 *  the page it shows, in the page's CSS pixels (not the image's). */
export type Frame = { data: string; width: number; height: number; type?: "jpeg" | "png" };

export type LiveTransport = {
  /** A finger went down, moved, or lifted after moving: what is under it
   *  scrolls. */
  drag(phase: "start" | "move" | "end", point?: Point): void;
  /** A tap, clicked where it landed. With `focus`, whether it left something
   *  that takes text focused (a field, in a frame or not: not the frame
   *  itself, a tick box or a button), so the keyboard may stay. */
  tap(point: Point, focus: boolean): Promise<boolean>;
  /** Text typed on the phone, into whatever holds focus. */
  type(text: string): void;
  key(key: "Backspace" | "Enter"): void;
  close(): void;
};

export type TransportOptions = {
  /** The phone's pixel ratio: how sharp an image is worth asking for. */
  ratio: number;
  onFrame: (frame: Frame) => void;
  /** The connection dropped without `close()`. */
  onClose: () => void;
};

/** Opens the live view of the browser `host` names (Attempt.liveView.host). */
export function openTransport(host: string, options: TransportOptions): Promise<LiveTransport> {
  return isBoatLiveView(host) ? openBoatTransport(host, options) : openCdpTransport(host, options);
}
